import { attr, belongsTo } from '@ember-data/model';

import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';

import BaseModel, { type IResourceName } from './base-model';

import type SimulationItemPlanning from 'volta/models/simulation-item-planning';
import type SimulationScenario from 'volta/models/simulation-scenario';
import type SkuModel from 'volta/models/sku';
import type { IPlanningSettingData } from 'volta/models/types/planning-settings';
import type { IAggTasksViewMap } from 'volta/models/types/tasks';
import type {
  IApplyAutoItemSettingsCmd,
  IApplyAutoItemSettingsResult,
  IApplyMultipleItemSettingsCmd,
  IUpdateManySimulationItemSettingsCmd,
  IUpdateSimulationItemSettingCmd,
  IValidateManyForecastsCmd,
  IValidateManyForecastsResult,
  IValidateManyPlanningSettingsCmd,
  IValidateManyPlanningSettingsResult
} from './types/simulation-item-setting';
import type { ICmdResult } from 'volta/utils/api/jsonapi-types';
import type { IRunStalloneCmd, IStalloneInfo } from 'volta/models/types/simulation';
import { Type } from '@warp-drive/core-types/symbols';
import type { IForecastTypeSelection } from './types/forecasts';
import type { IWorkshopIdentityInfo } from './types/workshop';

export const SimulationItemSettingResourceName: IResourceName = {
  singular: 'simulationItemSetting',
  plural: 'simulationItemSettings'
};

export const SimulationItemSettingEvents = {
  ForecastValidated: 'ForecastValidated',
  ManyForecastsValidated: 'ManyForecastsValidated',
  ManyPlanningSettingsValidated: 'ManyPlanningSettingsValidated',
  ManySimulationItemSettingsUpdated: 'ManySimulationItemSettingsUpdated',
  PlanningSettingsValidated: 'PlanningSettingsValidated',
  SimulationItemSettingCreated: 'SimulationItemSettingCreated',
  SimulationItemSettingDeleted: 'SimulationItemSettingDeleted',
  SimulationItemSettingsApplied: 'SimulationItemSettingsApplied',
  SimulationItemSettingUpdated: 'SimulationItemSettingUpdated',
  StalloneCalculationDone: 'StalloneCalculationDone'
};

export const SimulationItemSettingCommands = {
  UpdateSimulationItemSetting: 'UpdateSimulationItemSetting',
  UpdateManySimulationItemSettings: 'UpdateManySimulationItemSettings',
  ApplyMultipleItemSettings: 'ApplyMultipleItemSettings',
  RunStallone: 'RunStallone',
  ApplyAutoItemSettings: 'ApplyAutoItemSettings',
  ValidatePlanningSettings: 'ValidatePlanningSettings',
  ValidateManyPlanningSettings: 'ValidateManyPlanningSettings',
  ValidateForecasts: 'ValidateForecasts',
  ValidateManyForecasts: 'ValidateManyForecasts'
};

export const SimulationItemSettingQueries = {
  forecastTs: 'forecastTs'
};

export default class SimulationItemSetting extends BaseModel {
  // Collection commands
  // ~~~~~~

  static runStallone = collectionCommand<IRunStalloneCmd, DoneResult, typeof SimulationItemSetting>(
    SimulationItemSettingCommands.RunStallone
  );

  static applyMultipleItemSettings = collectionCommand<
    IApplyMultipleItemSettingsCmd,
    DoneResult,
    typeof SimulationItemSetting
  >(SimulationItemSettingCommands.ApplyMultipleItemSettings);

  static applyAutoItemSettings = collectionCommand<
    IApplyAutoItemSettingsCmd,
    ICmdResult<IApplyAutoItemSettingsResult>,
    typeof SimulationItemSetting
  >(SimulationItemSettingCommands.ApplyAutoItemSettings);

  static updateManySimulationItemSettings = collectionCommand<
    IUpdateManySimulationItemSettingsCmd,
    SimulationItemSetting[],
    typeof SimulationItemSetting
  >(SimulationItemSettingCommands.UpdateManySimulationItemSettings);

  static validateManyPlanningSettings = collectionCommand<
    IValidateManyPlanningSettingsCmd,
    ICmdResult<IValidateManyPlanningSettingsResult>,
    typeof SimulationItemSetting
  >(SimulationItemSettingCommands.ValidateManyPlanningSettings);

  static validateManyForecasts = collectionCommand<
    IValidateManyForecastsCmd,
    ICmdResult<IValidateManyForecastsResult>,
    typeof SimulationItemSetting
  >(SimulationItemSettingCommands.ValidateManyForecasts);

  // Resource commands
  // ~~~~~~

  updateSimulationItemSetting = command<IUpdateSimulationItemSettingCmd, SimulationItemSetting>(
    SimulationItemSettingCommands.UpdateSimulationItemSetting
  );

  validatePlanningSettings = command<{ scenarioId: string }, SimulationItemSetting>(
    SimulationItemSettingCommands.ValidatePlanningSettings
  );

  validateForecasts = command<{ scenarioId: string }, SimulationItemSetting>(
    SimulationItemSettingCommands.ValidateForecasts
  );

  // Properties
  // ~~~~~~
  @attr('string') simulationId!: string;
  @attr('string') scenarioId!: string;
  @attr('number') leadTime?: number;
  @attr('number') minOrderQty?: number;
  @attr('number') inventoryUnitPrice?: number;
  @attr('number') salesUnitPrice?: number;
  @attr('number') orderMultiple?: number;
  @attr('date') settingsValidatedAt?: Date;
  @attr('string') settingsValidatedBy?: string;
  @attr('date') forecastValidatedAt?: Date;
  @attr('string') forecastValidatedBy?: Date;
  @attr() settings!: IPlanningSettingData;
  @attr() stallone!: IStalloneInfo;
  @attr('array') forecastTypes?: IForecastTypeSelection[];
  /**
   * Workshop information
   */
  @attr() workshop?: IWorkshopIdentityInfo;

  /**
   * Item settings tasks info by type
   */
  @attr() tasks!: IAggTasksViewMap;

  // Relationships
  @belongsTo('simulation-item-planning', { async: false, inverse: 'itemSetting' })
  planning!: SimulationItemPlanning;

  @belongsTo('sku', { async: false, inverse: null })
  sku!: SkuModel;

  @belongsTo('simulation-scenario', { async: false, inverse: 'itemSettings' })
  scenario!: SimulationScenario;

  // Getters & CPs
  // ~~~~~~

  get stalloneStatus() {
    return this.stallone.status;
  }

  get stalloneLastSuggestion() {
    return this.stallone.lastSuggestion;
  }

  get stalloneLastStats() {
    return this.stallone.lastStats;
  }

  get skuId() {
    return this.sku.id;
  }

  get partCode() {
    return this.sku.part.code;
  }

  get skuDescription() {
    return this.sku.skuDescription;
  }

  get isBuffered() {
    return this.settings.methodology === 'BUFFER';
  }

  get isManaged() {
    return this.settings.methodology && this.settings.methodology !== 'NONE';
  }

  get isValidated() {
    return this.settingsValidatedAt !== undefined && this.forecastValidatedAt !== undefined;
  }

  [Type] = 'simulation-item-setting' as const;
}
